import React, { useState } from "react";

/**
 * CollabToolbar — one reusable action + collaboration bar for every LAMKO form.
 * Rendered by FormGate at the TOP and BOTTOM of each unlocked form, so all 6
 * forms share the exact same buttons, wording, styling and behaviour.
 *
 * Actions: Save draft, Assign to a colleague, Reset, Submit (primary).
 * Submit/Reset drive the form's own submit/reset buttons, so no form needs
 * custom changes. Assign transfers the SAME draft to a colleague in the same
 * organization (name, email, department, note) via one secure resume link.
 */

const API = process.env.REACT_APP_BACKEND_URL || "";

function readDraftData() {
  try {
    const key = "lamko_draft_" + window.location.pathname;
    const raw = window.localStorage.getItem(key);
    const v = raw ? JSON.parse(raw) : null;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return v.data && typeof v.data === "object" && !Array.isArray(v.data) ? v.data : v;
    }
  } catch (e) {}
  return {};
}
function findEmail(flat) {
  try {
    const vals = Object.keys(flat).map((k) => flat[k]).filter((x) => typeof x === "string");
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const hit = vals.find((x) => re.test(x.trim()));
    return hit ? hit.trim() : "";
  } catch (e) {
    return "";
  }
}
function slugOf() {
  return (window.location.pathname.split("/").filter(Boolean).pop() || "form").toLowerCase();
}
// Click the form's real Submit / Reset button (never a button inside this bar).
function clickFormButton(re) {
  try {
    const btns = Array.from(document.querySelectorAll("button")).filter((b) => !b.closest(".lamko-collab-bar"));
    const t = btns.find((b) => re.test((b.textContent || "").trim()));
    if (t) {
      t.click();
      return true;
    }
  } catch (e) {}
  return false;
}

const C = {
  barTop: { border: "1px solid #e5e7eb", borderRadius: 12, background: "#ffffff", padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", margin: "0 0 18px" },
  barBottom: { border: "1px solid #e5e7eb", borderRadius: 12, background: "#ffffff", padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.09)", margin: "18px 0 0" },
  row: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 },
  primary: { border: "none", borderRadius: 8, padding: "11px 22px", fontSize: 15, fontWeight: 700, cursor: "pointer", background: "#ea580c", color: "#fff", boxShadow: "0 1px 2px rgba(234,88,12,0.4)" },
  ghost: { border: "1px solid #d1d5db", borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#111827" },
  input: { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 8, padding: "9px 12px", fontSize: 14, marginTop: 4 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151", marginTop: 10, display: "block" },
  badge: { fontSize: 12, fontWeight: 700, color: "#9a3412", background: "#ffedd5", borderRadius: 999, padding: "4px 10px", whiteSpace: "nowrap" },
  note: { fontSize: 12, color: "#6b7280", marginTop: 8 },
  guide: { fontSize: 13, color: "#374151", fontWeight: 600, maxWidth: 680 },
};

export default function CollabToolbar(props) {
  const bottom = props && props.position === "bottom";
  const resumed = /[?&]draft=/.test(window.location.search);
  const [status, setStatus] = useState(resumed ? "Returned to You" : "Ready for Submission");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [resumeUrl, setResumeUrl] = useState("");
  const [showAssign, setShowAssign] = useState(false);
  const [me, setMe] = useState(findEmail(readDraftData()));
  const [cName, setCName] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cDept, setCDept] = useState("");
  const [cNote, setCNote] = useState("");

  async function saveDraft() {
    setBusy(true);
    setMsg("");
    setResumeUrl("");
    try {
      const data = readDraftData();
      const r = await fetch(API + "/api/collab/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: slugOf(), path: window.location.pathname, email: findEmail(data), data: data }),
      });
      const j = await r.json();
      if (j && j.ok && j.id && j.token) {
        setResumeUrl(window.location.origin + window.location.pathname + "?draft=" + j.id + "&t=" + j.token);
        setStatus("Draft Saved");
        setMsg("Your progress is saved. Keep the resume link below to continue later, or share it with a colleague.");
      } else {
        setMsg((j && j.error) || "Could not save the draft. Please try again.");
      }
    } catch (e) {
      setMsg("Could not save the draft. Please try again.");
    }
    setBusy(false);
  }

  async function assign(e) {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const data = readDraftData();
      const r = await fetch(API + "/api/collab/assign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: slugOf(),
          path: window.location.pathname,
          from_email: (me || "").trim(),
          colleague_name: (cName || "").trim(),
          colleague_email: (cEmail || "").trim(),
          department: (cDept || "").trim(),
          note: (cNote || "").trim(),
          data: data,
        }),
      });
      const j = await r.json();
      if (j && j.ok) {
        setStatus("Awaiting Colleague Input");
        setShowAssign(false);
        setMsg((cName || "Your colleague") + " has been emailed a secure link to continue this same form. If they do not receive it in their inbox, ask them to check spam or junk.");
      } else {
        setMsg((j && j.error) || "Could not assign. Please check the details and try again.");
      }
    } catch (e2) {
      setMsg("Could not assign. Please try again.");
    }
    setBusy(false);
  }

  function copy(e) {
    try {
      navigator.clipboard.writeText(resumeUrl);
      e.target.textContent = "Copied";
    } catch (x) {}
  }

  return (
    <div className="lamko-collab-bar" style={bottom ? C.barBottom : C.barTop}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div style={C.guide}>This form can be completed by one person or by multiple colleagues within the same organization.</div>
        <span style={C.badge}>{status}</span>
      </div>

      <div style={C.row}>
        <button type="button" style={C.ghost} disabled={busy} onClick={saveDraft}>
          {busy ? "Working..." : "Save draft"}
        </button>
        <button type="button" style={C.ghost} disabled={busy} onClick={() => setShowAssign(!showAssign)}>
          Assign to a colleague
        </button>
        <button type="button" style={C.ghost} onClick={() => { if (window.confirm("Clear everything entered on this form?")) clickFormButton(/reset/i); }}>
          Reset
        </button>
        <button type="button" style={Object.assign({ marginLeft: "auto" }, C.primary)} onClick={() => clickFormButton(/submit/i)}>
          Submit &rarr;
        </button>
      </div>

      <p style={C.note}>
        If you do not receive the notification email in your inbox, please check your spam or junk folder. Some automated
        emails may be filtered by your organization&rsquo;s email settings.
      </p>

      {resumeUrl ? (
        <div style={{ marginTop: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Resume link (save this):</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input readOnly value={resumeUrl} onFocus={(e) => e.target.select()} style={C.input} />
            <button type="button" style={C.primary} onClick={copy}>
              Copy
            </button>
          </div>
        </div>
      ) : null}

      {showAssign ? (
        <form onSubmit={assign} style={{ marginTop: 12, borderTop: "1px solid #eef0f2", paddingTop: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Hand off to a colleague</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            They receive one secure link that opens this same form with everything you entered. Must be the same
            organization email domain.
          </div>
          <label style={C.label}>Your work email</label>
          <input style={C.input} type="email" value={me} onChange={(e) => setMe(e.target.value)} placeholder="you@company.com" />
          <label style={C.label}>Colleague name</label>
          <input style={C.input} value={cName} onChange={(e) => setCName(e.target.value)} />
          <label style={C.label}>Colleague work email</label>
          <input style={C.input} type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} placeholder="colleague@company.com" />
          <label style={C.label}>Department (optional)</label>
          <input style={C.input} value={cDept} onChange={(e) => setCDept(e.target.value)} />
          <label style={C.label}>Note to colleague (optional)</label>
          <textarea style={Object.assign({}, C.input, { minHeight: 70, resize: "vertical" })} value={cNote} onChange={(e) => setCNote(e.target.value)} />
          <p style={C.note}>
            If your colleague does not receive the email, ask them to check spam or junk; some organizations filter
            automated messages.
          </p>
          <div style={{ marginTop: 10 }}>
            <button type="submit" style={C.primary} disabled={busy}>
              {busy ? "Sending..." : "Send to colleague"}
            </button>
          </div>
        </form>
      ) : null}

      {msg ? <p style={{ fontSize: 13, color: "#374151", marginTop: 10 }}>{msg}</p> : null}
    </div>
  );
}
